import React, { useState, useEffect } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { usePartner } from '../../context/PartnerContext';
import { useSearchParams, Link, useNavigate } from "react-router-dom";

const ROLES = [
  { value: "trader",   name: "Trader",   desc: "Track and showcase your trading performance" },
  { value: "investor", name: "Investor", desc: "Allocate capital to verified traders" },
  { value: "analyst",  name: "Analyst",  desc: "Publish signals and market analysis" },
];

const RegisterPage = () => {
  usePageTitle("Create Account");
  const { appName, partner, logoUrl, loading: partnerLoading, tagline } = usePartner();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [refCode] = useState(searchParams.get("ref") || "");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState(searchParams.get("role") || "trader");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const providerMode = partner && partner.signup && partner.signup.providerMode ? partner.signup.providerMode : "open";
  const visibleRoles = partnerLoading ? [] : (providerMode === "appointed" ? ROLES.filter((r) => r.value === "investor") : ROLES);
  useEffect(() => {
    if (!partnerLoading && visibleRoles.length > 0 && !visibleRoles.some((r) => r.value === role)) {
      setRole(visibleRoles[0].value);
    }
  }, [partnerLoading, providerMode, role]);

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token");
    if (token) {
      const r = localStorage.getItem("user_role") || "trader";
      navigate("/" + r);
    }
    const r = searchParams.get("role");
    if (r) setRole(r);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setBusy(true);
    setError("");
    try {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, name: fullName, primary_role: role, ref_code: refCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || "Registration failed");
      navigate("/check-email?email=" + encodeURIComponent(email));
    } catch (err) {
      setError(err.message || "Registration failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-panel">
        <div className="auth-brand">
          <div className="auth-brand-logo" style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>{(logoUrl || (!partner && !partnerLoading)) && <img src={logoUrl || "/logo.jpeg"} alt={appName || "CrunchAlpha"} style={{height:32,width:32,borderRadius:6,objectFit:"cover"}} onError={e=>e.target.style.display="none"}/>} {appName || "CrunchAlpha"}</div>
          {(partner ? tagline : "Risk Controlled Copy Trading") && <div className="auth-brand-tagline">{partner ? tagline : "Risk Controlled Copy Trading"}</div>}
        </div>
        <div className="auth-card">
          <h2>Create your account</h2>
          <p className="auth-subtitle">Get started — it only takes a minute.</p>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={onSubmit} className="auth-form">
            <label>
              <span>Full name</span>
              <input type="text" required autoComplete="name" value={fullName}
                onChange={(e) => setFullName(e.target.value)} placeholder="Your full name" />
            </label>
            <label>
              <span>Email address</span>
              <input type="email" required autoComplete="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </label>
            <label>
              <span>Password</span>
              <div style={{ position:"relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  required autoComplete="new-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 8 characters" minLength={8}
                  style={{ paddingRight:"44px" }}
                />
                <button type="button" tabIndex={-1}
                  onClick={() => setShowPassword(v => !v)}
                  style={{ position:"absolute", right:"12px", top:"50%", transform:"translateY(-50%)",
                    background:"none", border:"none", cursor:"pointer", color:"var(--text-faint)",
                    padding:"2px", display:"flex", alignItems:"center" }}>
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M3 3l14 14M10 4C5.6 4 2 10 2 10s1.4 2.2 3.8 4M10 16c4.4 0 8-6 8-6s-1.4-2.2-3.8-4"/><circle cx="10" cy="10" r="2.5"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
                      <path d="M2 10s3.6-6 8-6 8 6 8 6-3.6 6-8 6-8-6-8-6z"/><circle cx="10" cy="10" r="2.5"/>
                    </svg>
                  )}
                </button>
              </div>
              {password.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
                    {[
                      password.length >= 8,
                      /[A-Z]/.test(password),
                      /[a-z]/.test(password),
                      /[0-9]/.test(password),
                    ].map((ok, i) => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2,
                        background: ok ? "var(--accent)" : "var(--border)" }} />
                    ))}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", display: "flex", flexWrap: "wrap", gap: "4px 12px" }}>
                    {[
                      { label: "8+ chars", ok: password.length >= 8 },
                      { label: "Uppercase", ok: /[A-Z]/.test(password) },
                      { label: "Lowercase", ok: /[a-z]/.test(password) },
                      { label: "Number", ok: /[0-9]/.test(password) },
                    ].map(r => (
                      <span key={r.label} style={{ color: r.ok ? "var(--accent)" : "var(--text-faint)" }}>
                        {r.ok ? "" : "○"} {r.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </label>
            <div>
              <div style={{ fontSize:"13px", fontWeight:500, color:"var(--text-muted)", marginBottom:"8px" }}>
                I want to join as
              </div>
              {providerMode === "appointed" && (
                <div style={{ fontSize:"12px", color:"var(--text-muted)", marginBottom:"10px", lineHeight:1.5 }}>
                  Trader and analyst accounts in this community are appointed by the community admin. You can join as an investor.
                </div>
              )}
              <div className="role-grid">
                {visibleRoles.map((r) => (
                  <div key={r.value}
                    className={"role-option" + (role === r.value ? " selected" : "")}
                    onClick={() => setRole(r.value)}
                    role="radio" aria-checked={role === r.value}>
                    <div className="role-text">
                      <div className="role-name">{r.name}</div>
                      <div className="role-desc">{r.desc}</div>
                    </div>
                    <div className="role-check">
                      {role === r.value && <div className="role-check-dot" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <button type="submit" className="auth-button" disabled={busy}>
              {busy ? "Creating account..." : "Create account"}
            </button>
          </form>
          <p style={{ fontSize:"11px", color:"var(--text-faint)", textAlign:"center", marginTop:"14px", lineHeight:1.5 }}>
            By creating an account you agree to our <Link to="/legal/terms">Terms of Service</Link> and <Link to="/legal/privacy">Privacy Policy</Link>.
          </p>
        </div>
        <p className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
